import { Fragment, type ReactNode } from "react";

export type MarketingSectionId = string;
export type MarketingCtaSlotId = string;

export type MarketingPageRenderContext = {
  getCtaCopy: (slotId: MarketingCtaSlotId) => string | undefined;
  experimentId?: string;
  variantId: string;
};

export type MarketingPageSection = {
  id: MarketingSectionId;
  render: (context: MarketingPageRenderContext) => ReactNode;
};

export type MarketingCtaSlot = {
  slotId: MarketingCtaSlotId;
  defaultCopy: string;
  approvedCopy: string[];
};

export type MarketingPageConfig = {
  pageKey: string;
  sections: MarketingPageSection[];
  defaultSectionOrder: MarketingSectionId[];
  requiredSectionIds: MarketingSectionId[];
  ctaSlots: MarketingCtaSlot[];
};

export type MarketingPageVariantConfig = {
  sectionOrder?: MarketingSectionId[];
  ctaCopy?: Record<MarketingCtaSlotId, string>;
};

type MarketingPageRendererProps = {
  config: MarketingPageConfig;
  variant?: MarketingPageVariantConfig | null;
  experimentId?: string;
  variantId?: string;
};

function normalizeSectionOrder(config: MarketingPageConfig, variant?: MarketingPageVariantConfig | null) {
  const registeredIds = new Set(config.sections.map((section) => section.id));
  const defaultOrder = config.defaultSectionOrder.filter((sectionId) => registeredIds.has(sectionId));
  const requestedOrder = variant?.sectionOrder?.filter((sectionId) => registeredIds.has(sectionId)) || [];
  const orderedIds = requestedOrder.length ? requestedOrder : defaultOrder;
  const missingRequiredIds = config.requiredSectionIds.filter((sectionId) => !orderedIds.includes(sectionId));

  return [...orderedIds, ...missingRequiredIds.filter((sectionId) => registeredIds.has(sectionId))];
}

function normalizeCtaCopy(config: MarketingPageConfig, variant?: MarketingPageVariantConfig | null) {
  return config.ctaSlots.reduce<Record<string, string>>((copyBySlot, slot) => {
    const requestedCopy = variant?.ctaCopy?.[slot.slotId];
    copyBySlot[slot.slotId] = requestedCopy && slot.approvedCopy.includes(requestedCopy)
      ? requestedCopy
      : slot.defaultCopy;

    return copyBySlot;
  }, {});
}

export function MarketingPageRenderer({ config, experimentId, variant, variantId = "control" }: MarketingPageRendererProps) {
  const sectionsById = new Map(config.sections.map((section) => [section.id, section]));
  const sectionOrder = normalizeSectionOrder(config, variant);
  const ctaCopyBySlot = normalizeCtaCopy(config, variant);

  // The agent can only reorder registered sections and choose approved CTA copy from page config.
  const context: MarketingPageRenderContext = {
    experimentId,
    getCtaCopy: (slotId) => ctaCopyBySlot[slotId],
    variantId,
  };

  return (
    <>
      {sectionOrder.map((sectionId) => {
        const section = sectionsById.get(sectionId);

        return section ? <Fragment key={`${config.pageKey}-${section.id}`}>{section.render(context)}</Fragment> : null;
      })}
    </>
  );
}
